import React, { useState, useEffect, Suspense } from 'react';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import auditService from '../../../services/AuditService';
import { useParams, useNavigate } from 'react-router-dom';
import FolderSpecialRoundedIcon from '@mui/icons-material/FolderSpecialRounded';
import DynamicTabs from '../../common/DynamicTabs';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DataTable from '../../common/DataGrid';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CommentIcon from '@mui/icons-material/Comment';
import SendIcon from '@mui/icons-material/Send';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import MarkChatUnreadOutlinedIcon from '@mui/icons-material/MarkChatUnreadOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import appService from '../../../services/ApplicationService';
import Divider from '@mui/material/Divider';
import IASideBar from './IASideBar';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import userService from '../../../services/UserService';
import roleService from '../../../services/RoleService';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer as RechartsResponsiveContainer } from 'recharts';
import CloseIcon from '@mui/icons-material/Close';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { reviewNotesService } from '../../../services/ReviewNotesService';
import RefreshIcon from '@mui/icons-material/Refresh';

const TABS = [
    {
        value: '1',
        label: 'Workpapers',
        content: null // Will be rendered dynamically
    },
    { value: '2', label: 'Planning Documents', content: <div /> },
    { value: '3', label: 'Deficiencies', content: <div /> },
    { value: '4', label: 'Remediation', content: <div /> },
    { value: '5', label: 'Document Requests', content: <div /> },
    { value: '6', label: 'Risk Assessments', content: <div /> },
];

const globalStyles = `
    @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
    }
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
    }
    @keyframes shimmer {
        0% { background-position: -1000px 0; }
        100% { background-position: 1000px 0; }
    }
`;

const Workpapers = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedProject, setSelectedProject] = useState(null);
    const [apps, setApps] = useState([]);
    const [selectedApp, setSelectedApp] = useState(null);
    const [workpapers, setWorkpapers] = useState([]);
    const [companyID, setCompanyID] = useState(null);
    const [appID, setAppID] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [activeFilter, setActiveFilter] = useState(() => {
        const savedFilter = localStorage.getItem('workpaperQueueFilter');
        return savedFilter || 'all';
    });
    const [selectedType, setSelectedType] = useState(() => {
        const savedType = localStorage.getItem('selectedWorkpaperType');
        return savedType || 'All';
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const [assigneeSearch, setAssigneeSearch] = useState('');
    const [selectedFilters, setSelectedFilters] = useState({
        type: localStorage.getItem('selectedWorkpaperType') || 'All',
        status: 'All',
        assignee: 'All'
    });
    const [filteredWorkpapers, setFilteredWorkpapers] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [userDetails, setUserDetails] = useState({});
    const [statsDialogOpen, setStatsDialogOpen] = useState(false);
    const [auditUsers, setAuditUsers] = useState([]);
    const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
    const [selectedAssignee, setSelectedAssignee] = useState('');
    const [assignmentLoading, setAssignmentLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    // Add a state to track when cache is being used
    const [usingCache, setUsingCache] = useState(false);
    const [cacheTimestamp, setCacheTimestamp] = useState(null);

    // Get unique values for filters
    const filterOptions = React.useMemo(() => {
        if (!Array.isArray(workpapers)) return {
            types: ['All'],
            statuses: ['All'],
            assignees: ['All']
        };

        const types = new Set(workpapers.map(wp => wp.TYPE || 'Unspecified'));
        const statuses = new Set(workpapers.map(wp => wp.STATUS || 'Unspecified'));
        const assignees = new Set(
            workpapers
                .map(wp => {
                    const details = Array.isArray(wp.WORKPAPER_DETAILS) ? wp.WORKPAPER_DETAILS[0] : wp.WORKPAPER_DETAILS;
                    const assignedTo = details?.ASSIGNED_TO;
                    return assignedTo && assignedTo !== '-' ? assignedTo : 'Unassigned';
                })
        );

        return {
            types: ['All', ...Array.from(types)].filter(Boolean),
            statuses: ['All', ...Array.from(statuses)].filter(Boolean),
            assignees: ['All', 'Unassigned', ...Array.from(assignees).filter(a => a !== 'Unassigned')].filter(Boolean)
        };
    }, [workpapers]);

    // Function to save workpapers data to localStorage with cache timestamp
    const saveWorkpapersToCache = (projectId, companyId, appId, data) => {
        try {
            // Create a cache key based on project, company, and app
            const cacheKey = `workpapers_${projectId}_${companyId}_${appId || 'all'}`;
            const cacheData = {
                timestamp: new Date().getTime(),
                data: data
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (error) {
            console.error('Failed to save workpapers to cache:', error);
        }
    };

    // Function to get workpapers data from localStorage
    const getWorkpapersFromCache = (projectId, companyId, appId) => {
        try {
            // Create a cache key based on project, company, and app
            const cacheKey = `workpapers_${projectId}_${companyId}_${appId || 'all'}`;
            const cachedData = localStorage.getItem(cacheKey);
            
            if (!cachedData) {
                return null;
            }
            
            const parsedData = JSON.parse(cachedData);
            const cacheAge = new Date().getTime() - parsedData.timestamp;
            const cacheMaxAge = 30 * 60 * 1000; // 30 minutes in milliseconds
            
            // Return the cached data if it's not too old
            if (cacheAge < cacheMaxAge) {
                setCacheTimestamp(new Date(parsedData.timestamp).toLocaleTimeString());
                return parsedData.data;
            } else {
                return null;
            }
        } catch (error) {
            console.error('Failed to get workpapers from cache:', error);
            return null;
        }
    };

    // Clear cache when project changes
    useEffect(() => {
        if (id) {
            // Clear any cached data related to previous projects
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('workpapers_') && !key.includes(`_${id}_`)) {
                    localStorage.removeItem(key);
                }
            });
        }
    }, [id]);

    useEffect(() => {
        localStorage.setItem('selectedWorkpaperType', selectedFilters.type);
    }, [selectedFilters.type]);

    useEffect(() => {
        localStorage.setItem('workpaperQueueFilter', activeFilter);
    }, [activeFilter]);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = globalStyles;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        const fetchCurrentUser = async () => {
            const user = await userService.fetchCurrentUser();
            if (user) {
                setCurrentUser(user);
            }
        };

        fetchCurrentUser();
    }, []);

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                const users = await roleService.fetchUsers();
                const userMap = {};
                users.forEach(user => {
                    if (user.user?.first_name && user.user?.last_name) {
                        userMap[user.user.username] = `${user.user.first_name} ${user.user.last_name}`;
                    }
                });
                setUserDetails(userMap);
            } catch (error) {
                console.error('Error fetching user details:', error);
            }
        };
        fetchUserDetails();
    }, []);

    useEffect(() => {
        const fetchProjectData = async () => {
            try {
                setIsLoading(true);
                const projectRecord = await auditService.fetchProjectsById(id);
                if (projectRecord) {
                    setSelectedProject(projectRecord);
                    setCompanyID(projectRecord.COMPANY_ID);
                    
                    const appRecord = await appService.fetchAppsByCompany(projectRecord.COMPANY_ID);
                    if (appRecord) {
                        setApps(appRecord);
                        // Don't select a specific app by default
                        setSelectedApp(null);
                        setAppID(null);
                        // By default show all workpapers instead of just the user's queue
                        setActiveFilter('all');
                        // Fetch all workpapers instead of for a specific app
                        await fetchAllWorkpapers(projectRecord.COMPANY_ID);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setSelectedProject(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProjectData();
    }, [id]);

    // Modified function to fetch workpapers for all apps with caching
    const fetchAllWorkpapers = async (company_id) => {
        if (!company_id) {
            setIsDataLoading(false);
            return;
        }
        
        // Try to load from cache first
        const cachedWorkpapers = getWorkpapersFromCache(id, company_id, null);
        if (cachedWorkpapers) {
            setUsingCache(true);
            setWorkpapers(cachedWorkpapers);
            // Still fetch fresh data in the background
        } else {
            setUsingCache(false);
        }
        
        try {
            setIsDataLoading(true);
            
            // Get all apps for this company
            const appRecord = await appService.fetchAppsByCompany(company_id);
            
            if (!appRecord || appRecord.length === 0) {
                setWorkpapers([]);
                setFilteredWorkpapers([]); // Also update filtered workpapers
                setIsDataLoading(false);
                return;
            }
            
            // Process apps one by one instead of using Promise.all to avoid overwhelming the server
            let allWorkpapers = [];
            
            for (let i = 0; i < appRecord.length; i++) {
                const app = appRecord[i];
                
                try {
                    // Pass project ID (id) to fetch only workpapers for this project
                    const controls = await auditService.fetchWorkpapersByApp(app.id, company_id, id);
                    if (controls && Array.isArray(controls)) {
                        const filteredControls = controls.filter(control => control.FOLDER === "Workpaper");
                        allWorkpapers = [...allWorkpapers, ...filteredControls];
                    }
                } catch (appError) {
                    console.error(`Error fetching workpapers for app ${app.id}:`, appError);
                }
            }
            
            if (allWorkpapers.length === 0) {
                setWorkpapers([]);
                setFilteredWorkpapers([]); // Explicitly set filtered workpapers to empty
                setIsDataLoading(false);
                return;
            }
            
            // Process workpapers in smaller batches to avoid overwhelming the API
            const BATCH_SIZE = 10;
            let processedWorkpapers = [];
            
            for (let i = 0; i < allWorkpapers.length; i += BATCH_SIZE) {
                const batch = allWorkpapers.slice(i, i + BATCH_SIZE);
                
                const batchResults = await Promise.all(batch.map(async (wp) => {
                    try {
                        // First get workpaper details
                        const workpaperDetails = await auditService.fetchWorkpaperDetailsById(wp.id);
                        const workpaperDetailsId = workpaperDetails && workpaperDetails.length > 0 ? workpaperDetails[0].id : null;

                        // Then fetch PBC requests and review notes using workpaper details ID
                        const [pbcRequests, reviewNotes] = await Promise.all([
                            auditService.fetchAuditRequestsByWorkpaper(wp.id),
                            workpaperDetailsId ? reviewNotesService.getReviewNotes(workpaperDetailsId) : []
                        ]);
                        
                        // Get the most recent request's status
                        const latestRequest = pbcRequests && pbcRequests.length > 0 ? pbcRequests[0] : null;
                        // Count unresolved review notes
                        const unresolvedCount = reviewNotes ? reviewNotes.filter(note => !note.resolved).length : 0;
                        
                        return {
                            ...wp,
                            PBC_STATUS: latestRequest ? latestRequest.STATUS : 'No Requests',
                            UNRESOLVED_NOTES: unresolvedCount,
                            WORKPAPER_DETAILS: workpaperDetails
                        };
                    } catch (error) {
                        console.error('Error fetching workpaper details for ID:', wp.id, error);
                        return {
                            ...wp,
                            PBC_STATUS: 'No Requests',
                            UNRESOLVED_NOTES: 0
                        };
                    }
                }));
                
                processedWorkpapers = [...processedWorkpapers, ...batchResults];
                
                // Update state incrementally so user sees some data even if not all is loaded
                // Only update if we're not using cache or this is the first batch
                if ((i === 0 && !usingCache) || !cachedWorkpapers) {
                    setWorkpapers(processedWorkpapers);
                }
            }
            
            // Cache the fresh workpapers data
            saveWorkpapersToCache(id, company_id, null, processedWorkpapers);
            
            // Update state with fresh data
            setWorkpapers(processedWorkpapers);
            setUsingCache(false);
            
        } catch (error) {
            console.error('Failed to fetch workpapers:', error);
            if (!cachedWorkpapers) {
                setWorkpapers([]);
                setFilteredWorkpapers([]);
            }
        } finally {
            setIsDataLoading(false);
        }
    };

    useEffect(() => {
        if (!Array.isArray(workpapers)) {
            setFilteredWorkpapers([]);
            return;
        }

        let filtered = [...workpapers];

        // Apply type filter
        if (selectedFilters.type !== 'All') {
            filtered = filtered.filter(wp => wp.TYPE === selectedFilters.type);
        }

        // Apply status filter
        if (selectedFilters.status !== 'All') {
            filtered = filtered.filter(wp => wp.STATUS === selectedFilters.status);
        }

        // Apply assignee filter
        if (selectedFilters.assignee !== 'All') {
            filtered = filtered.filter(wp => {
                const workpaperDetails = Array.isArray(wp.WORKPAPER_DETAILS) ? wp.WORKPAPER_DETAILS[0] : wp.WORKPAPER_DETAILS;
                const assignedTo = workpaperDetails?.ASSIGNED_TO;
                
                if (selectedFilters.assignee === 'Unassigned') {
                    return !assignedTo || assignedTo === '-';
                }
                return assignedTo === selectedFilters.assignee;
            });
        }

        // Apply queue filter
        if (activeFilter === 'myQueue' && currentUser) {
            filtered = filtered.filter(wp => {
                const workpaperDetails = Array.isArray(wp.WORKPAPER_DETAILS) ? wp.WORKPAPER_DETAILS[0] : wp.WORKPAPER_DETAILS;
                return workpaperDetails?.ASSIGNED_TO === currentUser?.username;
            });
        }

        setFilteredWorkpapers(filtered);
    }, [workpapers, activeFilter, currentUser, selectedFilters]);

    // Modified function to fetch workpapers for a specific app with caching
    const fetchWorkpapers = async (app_id, company_id) => {
        if (!app_id || !company_id) return;
        
        // Try to load from cache first
        const cachedWorkpapers = getWorkpapersFromCache(id, company_id, app_id);
        if (cachedWorkpapers) {
            setUsingCache(true);
            setWorkpapers(cachedWorkpapers);
            // Still fetch fresh data in the background
        } else {
            setUsingCache(false);
        }
        
        try {
            setIsDataLoading(true);
            // Pass project ID (id) to fetch only workpapers for this project
            const controls = await auditService.fetchWorkpapersByApp(app_id, company_id, id);
            if (controls) {
                const workpapers = controls.filter(control => control.FOLDER === "Workpaper");
                // Fetch PBC status and review notes count for each workpaper
                const workpapersWithDetails = await Promise.all(workpapers.map(async (wp) => {
                    try {
                        // First get workpaper details
                        const workpaperDetails = await auditService.fetchWorkpaperDetailsById(wp.id);
                        const workpaperDetailsId = workpaperDetails && workpaperDetails.length > 0 ? workpaperDetails[0].id : null;

                        // Then fetch PBC requests and review notes using workpaper details ID
                        const [pbcRequests, reviewNotes] = await Promise.all([
                            auditService.fetchAuditRequestsByWorkpaper(wp.id),
                            workpaperDetailsId ? reviewNotesService.getReviewNotes(workpaperDetailsId) : []
                        ]);
                        
                        // Get the most recent request's status
                        const latestRequest = pbcRequests && pbcRequests.length > 0 ? pbcRequests[0] : null;
                        // Count unresolved review notes
                        const unresolvedCount = reviewNotes ? reviewNotes.filter(note => !note.resolved).length : 0;
                        
                        return {
                            ...wp,
                            PBC_STATUS: latestRequest ? latestRequest.STATUS : 'No Requests',
                            UNRESOLVED_NOTES: unresolvedCount,
                            WORKPAPER_DETAILS: workpaperDetails
                        };
                    } catch (error) {
                        console.error('Error fetching workpaper details:', error);
                        return {
                            ...wp,
                            PBC_STATUS: 'No Requests',
                            UNRESOLVED_NOTES: 0
                        };
                    }
                }));
                
                // Cache the fresh workpapers data
                saveWorkpapersToCache(id, company_id, app_id, workpapersWithDetails);
                
                // Only update state if we didn't use cache or this is the first load
                if (!usingCache || !cachedWorkpapers) {
                    setWorkpapers(workpapersWithDetails || []);
                }
            }
        } catch (error) {
            console.error('Failed to fetch workpapers:', error);
            if (!cachedWorkpapers) {
                setWorkpapers([]);
            }
        } finally {
            setIsDataLoading(false);
        }
    };

    const handleTitleClick = (row) => {
        // If no app is selected but we're showing a workpaper, get its app ID from the workpaper data
        let targetAppId = appID;
        if (!targetAppId && row.APP_NAME_ID) {
            // If we have the app ID directly in the row data
            targetAppId = row.APP_NAME_ID;
        } else if (!targetAppId && typeof row.APP_NAME === 'object' && row.APP_NAME.id) {
            // If APP_NAME is an object with ID
            targetAppId = row.APP_NAME.id;
        } else if (!targetAppId && typeof row.APP_NAME === 'string' && 
                  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(row.APP_NAME)) {
            // If APP_NAME is a UUID string
            targetAppId = row.APP_NAME;
        }

        // If we still don't have an app ID, try to find it from original workpaper data
        if (!targetAppId && row.ctrlID) {
            const originalWorkpaper = workpapers.find(wp => wp.id === row.ctrlID);
            if (originalWorkpaper && originalWorkpaper.APP_NAME) {
                if (typeof originalWorkpaper.APP_NAME === 'object' && originalWorkpaper.APP_NAME.id) {
                    targetAppId = originalWorkpaper.APP_NAME.id;
                } else if (typeof originalWorkpaper.APP_NAME === 'string') {
                    targetAppId = originalWorkpaper.APP_NAME;
                }
            }
        }

        // If we still don't have an app ID, use the first app as fallback
        if (!targetAppId && apps.length > 0) {
            targetAppId = apps[0].id;
        }

        const url = `/audit/workpapers/project/${id}/app/${targetAppId}/control/${row.ctrlID}`;
        window.open(url, '_blank');
    };

    const columns = [
        { 
            field: 'id', 
            headerName: '#', 
            width: 50,
            renderCell: (params) => (
                <span style={{ 
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%'
                }}>{params.value}</span>
            )
        },
        { 
            field: 'APP_NAME', 
            headerName: 'System Name', 
            flex: 1,
            renderCell: (params) => (
                <span
                    style={{ 

                        fontSize: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%'
                    }}
                  
                >
                    {params.value}
                </span>
            )
        },
        { 
            field: 'CONTROL_ID', 
            headerName: 'Control ID', 
            flex: 1,
            renderCell: (params) => (
                <span
                    style={{ 
                        cursor: 'pointer', 
                        textDecoration: 'underline',
                        fontSize: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%'
                    }}
                    onClick={() => handleTitleClick(params.row)}
                >
                    {params.value}
                </span>
            )
        },
        { 
            field: 'CONTROL_TITLE', 
            headerName: 'Control Name', 
            flex: 1,
            renderCell: (params) => (
                <span style={{ 
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%'
                }}>{params.value}</span>
            )
        },
        { 
            field: 'CURRENTLY_WITH', 
            headerName: 'Assignee', 
            flex: 1,
            renderCell: (params) => {
                if (!params.value || params.value === '-') return (
                    <span style={{ 
                        fontSize: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%'
                    }}>-</span>
                );
                const username = params.value;
                const fullName = userDetails[username];
                if (!fullName) return (
                    <span style={{ 
                        fontSize: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%'
                    }}>-</span>
                );
                
                const names = fullName.split(' ');
                const initials = `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
                
                return (
                    <mui.Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        height: '100%'
                    }}>
                        <Tooltip title={fullName} arrow>
                            <mui.Avatar 
                                sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    bgcolor: 'primary.main',
                                    fontSize: '0.75rem'
                                }}
                            >
                                {initials}
                            </mui.Avatar>
                        </Tooltip>
                    </mui.Box>
                );
            }
        },
        { 
            field: 'STATUS', 
            headerName: 'Status', 
            flex: 1,
            renderCell: (params) => {
                const status = params.value || '-';
                const getStatusStyle = (status) => {
                    switch(status.toLowerCase()) {
                        case 'complete':
                            return { 
                                border: '1px solid #e2e8f0',
                                background: '#f8fafc',
                                color: '#0f766e'
                            };
                        case 'in progress':
                            return { 
                                border: '1px solid #e2e8f0',
                                background: '#f8fafc',
                                color: '#1e40af'
                            };
                        case 'not started':
                            return { 
                                border: '1px solid #e2e8f0',
                                background: '#f8fafc',
                                color: '#854d0e'
                            };
                        case 'in review':
                            return { 
                                border: '1px solid #e2e8f0',
                                background: '#f8fafc',
                                color: '#15803d'
                            };
                        case 'rejected':
                            return { 
                                border: '1px solid #e2e8f0',
                                background: '#f8fafc',
                                color: '#991b1b'
                            };
                        default:
                            return { 
                                border: '1px solid #e2e8f0',
                                background: '#f8fafc',
                                color: '#64748b'
                            };
                    }
                };

                const style = getStatusStyle(status);
                
                return (
                    <mui.Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            justifyContent: 'center'
                        }}
                    >
                        <mui.Box
                            sx={{
                                ...style,
                                fontSize: '0.75rem',
                                py: 0.5,
                                px: 1.5,
                                borderRadius: '4px',
                                display: 'inline-block',
                                fontWeight: 500,
                                letterSpacing: '0.025em',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {status}
                        </mui.Box>
                    </mui.Box>
                );
            }
        },
        { 
            field: 'TYPE', 
            headerName: 'Type', 
            flex: 1,
            renderCell: (params) => (
                <span style={{ 
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    justifyContent: 'center'
                }}>{params.value}</span>
            )
        }
    ];

    const renderActionButton = (icon, title, status = null) => (params) => {
        const getPBCStatusColor = (status) => {
            switch(status?.toLowerCase()) {
                case 'pending':
                    return '#f59e0b'; // Amber
                case 'in review':
                    return '#3b82f6'; // Blue
                case 'completed':
                    return '#10b981'; // Green
                case 'evidence submitted':
                    return '#6366f1'; // Indigo
                case 'resubmission needed':
                    return '#ef4444'; // Red
                case 'no requests':
                    return '#94a3b8'; // Grey
                default:
                    return '#94a3b8'; // Grey
            }
        };

        const handlePBCClick = () => {
            const controlId = params.row.CONTROL_ID;
            const url = `/audit/auditrequests/${id}?control=${controlId}`;
            window.open(url, '_blank');
        };

        const isEvidenceSubmitted = status?.toLowerCase() === 'evidence submitted';
        const isCompleted = status?.toLowerCase() === 'completed';

        return (
            <mui.Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%'
            }}>
                <Tooltip title={status === 'No Requests' ? 'No requests yet' : `${title} - Request ${status}`} arrow>
                    <mui.Box sx={{ position: 'relative' }}>
                        <mui.IconButton 
                            size="small" 
                            onClick={handlePBCClick}
                            sx={{ 
                                position: 'relative',
                                padding: '4px',
                                '& .MuiSvgIcon-root': {
                                    fontSize: '0.875rem',
                                    color: status ? getPBCStatusColor(status) : 'inherit',
                                    animation: isEvidenceSubmitted ? 'pulse 2s infinite' : 'none'
                                },
                                ...(isEvidenceSubmitted && {
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(99, 102, 241, 0.2)'
                                    }
                                }),
                                cursor: 'pointer'
                            }}
                        >
                            {icon}
                        </mui.IconButton>
                        {isEvidenceSubmitted && (
                            <mui.Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: '#ef4444',
                                    border: '1px solid #fff',
                                    animation: 'pulse 2s infinite'
                                }}
                            />
                        )}
                        {isCompleted && (
                            <mui.Box
                                sx={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: 14,
                                    height: 14,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1.5px solid white',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                    '& svg': {
                                        fontSize: '0.625rem'
                                    }
                                }}
                            >
                                <CheckCircleIcon fontSize="inherit" />
                            </mui.Box>
                        )}
                    </mui.Box>
                </Tooltip>
            </mui.Box>
        );
    };

    const handleSingleAssign = (workpaper) => {
        setSelectedRows([workpaper.id]);
        setAssignmentDialogOpen(true);
    };

    const columnsWithActions = [
        ...columns,
        {
            field: 'pbc',
            headerName: 'PBC',
            flex: 1,
            sortable: false,
            renderCell: (params) => renderActionButton(
                <DescriptionOutlinedIcon />, 
                'Status:',
                params.row.PBC_STATUS
            )(params),
        },
        {
            field: 'signoff',
            headerName: 'Sign-Off',
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                const workpaperDetails = Array.isArray(params.row.WORKPAPER_DETAILS) ? 
                    params.row.WORKPAPER_DETAILS[0] : params.row.WORKPAPER_DETAILS;

                if (!workpaperDetails) return null;

                const phases = [
                    {
                        name: 'D',
                        tester: workpaperDetails.DESIGN_TESTER,
                        testerSignoff: workpaperDetails.DESIGN_TESTER_SIGNOFF,
                        reviewer: workpaperDetails.DESIGN_REVIEWER,
                        reviewerSignoff: workpaperDetails.DESIGN_REVIEWER_SIGNOFF,
                        fullName: 'Design'
                    },
                    {
                        name: 'I',
                        tester: workpaperDetails.INTERIM_TESTER,
                        testerSignoff: workpaperDetails.INTERIM_TESTER_SIGNOFF,
                        reviewer: workpaperDetails.INTERIM_REVIEWER,
                        reviewerSignoff: workpaperDetails.INTERIM_REVIEWER_SIGNOFF,
                        fullName: 'Interim'
                    },
                    {
                        name: 'R',
                        tester: workpaperDetails.RF_TESTER,
                        testerSignoff: workpaperDetails.RF_TESTER_SIGNOFF,
                        reviewer: workpaperDetails.RF_REVIEWER,
                        reviewerSignoff: workpaperDetails.RF_REVIEWER_SIGNOFF,
                        fullName: 'Rollforward'
                    },
                    {
                        name: 'F',
                        tester: workpaperDetails.FINAL_TESTER,
                        testerSignoff: workpaperDetails.FINAL_TESTER_SIGNOFF,
                        reviewer: workpaperDetails.FINAL_REVIEWER,
                        reviewerSignoff: workpaperDetails.FINAL_REVIEWER_SIGNOFF,
                        fullName: 'Final'
                    }
                ];

                const activePhases = phases.filter(phase => 
                    phase.tester || phase.testerSignoff || phase.reviewer || phase.reviewerSignoff
                );

                if (activePhases.length === 0) {
                    return (
                        <mui.Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: 1,
                            height: '100%',
                            minHeight: '40px',
                            justifyContent: 'center'
                        }}>
                            <Tooltip title="No sign-offs assigned" arrow>
                                <RadioButtonUncheckedIcon sx={{ 
                                    fontSize: '1rem',
                                    color: '#e2e8f0',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        color: '#94a3b8'
                                    }
                                }} />
                            </Tooltip>
                        </mui.Box>
                    );
                }

                return (
                    <mui.Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 1,
                        height: '100%',
                        minHeight: '40px',
                        justifyContent: 'center'
                    }}>
                        {activePhases.map((phase) => {
                            const isComplete = phase.testerSignoff && phase.reviewerSignoff;
                            const isPartial = phase.testerSignoff || phase.reviewerSignoff;
                            const hasAssignment = phase.tester || phase.reviewer;
                            
                            const testerName = phase.tester ? userDetails[phase.tester] : null;
                            const reviewerName = phase.reviewer ? userDetails[phase.reviewer] : null;

                            const tooltipContent = (
                                <mui.Box sx={{ p: 1 }}>
                                    <mui.Typography sx={{ 
                                        fontSize: '0.75rem', 
                                        fontWeight: 600,
                                        color: 'white',
                                        opacity: 0.9,
                                        mb: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}>
                                        <mui.Box
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                backgroundColor: isComplete ? '#10b981' : 
                                                       isPartial ? '#1976d2' : 
                                                       hasAssignment ? '#94a3b8' : '#e2e8f0',
                                                opacity: isComplete ? 1 :
                                                        isPartial ? 0.6 :
                                                        hasAssignment ? 0.4 : 0.2,
                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                cursor: 'pointer',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                '&:hover': {
                                                    transform: 'scale(1.3)',
                                                    opacity: isComplete ? 1 :
                                                            isPartial ? 0.8 :
                                                            hasAssignment ? 0.6 : 0.4,
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                    backgroundColor: isComplete ? '#10b981' : 
                                                                   isPartial ? '#1976d2' : 
                                                                   hasAssignment ? '#64748b' : '#94a3b8'
                                                }
                                            }}
                                        />
                                        {phase.fullName}
                                    </mui.Typography>
                                    {testerName && (
                                        <mui.Typography sx={{ 
                                            fontSize: '0.75rem',
                                            color: 'white',
                                            opacity: phase.testerSignoff ? 0.9 : 0.6,
                                            pl: 1.5
                                        }}>
                                            • {testerName} {phase.testerSignoff ? 
                                                `(${new Date(phase.testerSignoff).toLocaleDateString()})` : 
                                                '(Pending)'}
                                        </mui.Typography>
                                    )}
                                    {reviewerName && (
                                        <mui.Typography sx={{ 
                                            fontSize: '0.75rem',
                                            color: 'white',
                                            opacity: phase.reviewerSignoff ? 0.9 : 0.6,
                                            pl: 1.5
                                        }}>
                                            • {reviewerName} {phase.reviewerSignoff ? 
                                                `(${new Date(phase.reviewerSignoff).toLocaleDateString()})` : 
                                                '(Pending)'}
                                        </mui.Typography>
                                    )}
                                </mui.Box>
                            );

                            return (
                                <Tooltip
                                    key={phase.name}
                                    arrow
                                    title={tooltipContent}
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: '#1e293b',
                                                '& .MuiTooltip-arrow': {
                                                    color: '#1e293b'
                                                },
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                                            }
                                        }
                                    }}
                                >
                                    <mui.Box
                                        sx={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: '50%',
                                            backgroundColor: isComplete ? '#10b981' : 
                                                   isPartial ? '#1976d2' : 
                                                   hasAssignment ? '#94a3b8' : '#e2e8f0',
                                            opacity: isComplete ? 1 :
                                                    isPartial ? 0.6 :
                                                    hasAssignment ? 0.4 : 0.2,
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            cursor: 'pointer',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                            '&:hover': {
                                                transform: 'scale(1.3)',
                                                opacity: isComplete ? 1 :
                                                        isPartial ? 0.8 :
                                                        hasAssignment ? 0.6 : 0.4,
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                backgroundColor: isComplete ? '#10b981' : 
                                                               isPartial ? '#1976d2' : 
                                                               hasAssignment ? '#64748b' : '#94a3b8'
                                            }
                                        }}
                                    />
                                </Tooltip>
                            );
                        })}
                    </mui.Box>
                );
            }
        },
        {
            field: 'notes',
            headerName: 'Review Notes',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <mui.Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%'
                }}>
                    <Tooltip title={`${params.row.UNRESOLVED_NOTES} unresolved review notes`} arrow>
                        <mui.Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <mui.IconButton 
                                size="small"
                                sx={{ 
                                    padding: '4px',
                                    '& .MuiSvgIcon-root': {
                                        fontSize: '0.875rem',
                                        color: params.row.UNRESOLVED_NOTES > 0 ? '#f59e0b' : 'inherit'
                                    }
                                }}
                            >
                                <MarkChatUnreadOutlinedIcon />
                            </mui.IconButton>
                            {params.row.UNRESOLVED_NOTES > 0 && (
                                <mui.Box
                                    sx={{
                                        position: 'absolute',
                                        top: -2,
                                        right: -2,
                                        backgroundColor: '#f59e0b',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: 16,
                                        height: 16,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        border: '1.5px solid white'
                                    }}
                                >
                                    {params.row.UNRESOLVED_NOTES}
                                </mui.Box>
                            )}
                        </mui.Box>
                    </Tooltip>
                </mui.Box>
            )
        },
        {
            field: 'send',
            headerName: 'Assign',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <mui.Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%'
                }}>
                    <Tooltip title="Assign Workpaper" arrow>
                        <mui.IconButton 
                            size="small" 
                            onClick={() => handleSingleAssign(params.row)}
                            sx={{ 
                                padding: '4px',
                                '& .MuiSvgIcon-root': {
                                    fontSize: '0.875rem'
                                }
                            }}
                        >
                            <AssignmentTurnedInIcon />
                        </mui.IconButton>
                    </Tooltip>
                </mui.Box>
            ),
        },
    ];

    const rows = React.useMemo(() => {
        if (!Array.isArray(filteredWorkpapers)) return [];
        return filteredWorkpapers.map((wp, index) => {
            const workpaperDetails = Array.isArray(wp.WORKPAPER_DETAILS) ? wp.WORKPAPER_DETAILS[0] : wp.WORKPAPER_DETAILS;
            
            // Check if APP_NAME is a UUID and use APP_NAME.APP_NAME if available
            let appName = '-';
            let appNameId = null;
            
            // If it's an object with APP_NAME property, use that
            if (wp.APP_NAME && typeof wp.APP_NAME === 'object' && wp.APP_NAME.APP_NAME) {
                appName = wp.APP_NAME.APP_NAME;
                appNameId = wp.APP_NAME.id;
            } 
            // If it's a string that looks like a UUID, try to find the real name from apps array if available
            else if (wp.APP_NAME && typeof wp.APP_NAME === 'string' && 
                    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(wp.APP_NAME)) {
                // Find the matching app if it's in our apps list
                const matchingApp = apps.find(app => app.id === wp.APP_NAME);
                if (matchingApp) {
                    appName = matchingApp.APP_NAME;
                } else {
                    appName = 'Unknown App';
                }
                appNameId = wp.APP_NAME; // Store the UUID as the ID
            }
            // If it's a normal string, use it directly
            else if (wp.APP_NAME && typeof wp.APP_NAME === 'string') {
                appName = wp.APP_NAME;
            }
            
            return {
                id: index + 1,
                APP_NAME: appName,
                APP_NAME_ID: appNameId,
                CONTROL_ID: wp.CONTROL_DETAILS?.CONTROL_ID || '-',
                CONTROL_TITLE: wp.CONTROL_DETAILS?.CONTROL_TITLE || '-',
                CURRENTLY_WITH: workpaperDetails?.ASSIGNED_TO || '-',
                STATUS: wp.STATUS || '-',
                TYPE: wp.TYPE || '-',
                ctrlID: wp.id,
                PBC_STATUS: wp.PBC_STATUS,
                UNRESOLVED_NOTES: wp.UNRESOLVED_NOTES,
                WORKPAPER_DETAILS: wp.WORKPAPER_DETAILS
            };
        });
    }, [filteredWorkpapers, apps]);

    const handleSelectionChange = (newSelection) => {
        setSelectedRows(newSelection);
    };

    const tabs = TABS.map(tab => ({
        ...tab,
        content: tab.value === '1' ? (
            <Suspense fallback={<div>Loading...</div>}>
                <mui.Box sx={{ 
                    position: 'relative',
                    height: '100%',
                    minHeight: '60vh',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    p: 2
                }}>
                    {!isDataLoading ? (
                        <mui.Box sx={{ 
                            width: '100%',
                            height: 'calc(100vh - 300px)',
                            minHeight: '700px',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowX: 'auto',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                                width: '8px',
                                height: '8px',
                                display: 'block'
                            },
                            '&::-webkit-scrollbar-track': {
                                background: '#f1f1f1',
                                borderRadius: '4px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#888',
                                borderRadius: '4px',
                                '&:hover': {
                                    background: '#555'
                                }
                            }
                        }}>
                            {rows.length > 0 ? (
                                <DataTable
                                    key={`datatable-${appID || 'all'}`}
                                    rows={rows}
                                    columns={columnsWithActions}
                                    loading={isDataLoading}
                                    checkboxSelection
                                    onRowSelectionModelChange={handleSelectionChange}
                                    rowSelectionModel={selectedRows}
                                    disableRowSelectionOnClick
                                    autoHeight={false}
                                    height={600}
                                    slots={{
                                        baseCheckbox: (props) => (
                                            <Tooltip 
                                                title="Select workpapers to assign them in bulk" 
                                                arrow
                                                placement="top-start"
                                            >
                                                <mui.Checkbox {...props} />
                                            </Tooltip>
                                        )
                                    }}
                                    sx={{
                                        flex: 1,
                                        width: '100%',
                                        '& .MuiDataGrid-root': {
                                            height: '100% !important',
                                            border: 'none',
                                            '& .MuiDataGrid-main': {
                                                height: '100% !important',
                                            },
                                            '& .MuiDataGrid-virtualScroller': {
                                                height: '100% !important',
                                            },
                                            '& .MuiDataGrid-cell': {
                                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                                color: 'text.primary',
                                                fontSize: '0.75rem',
                                                padding: '12px 16px',
                                                '&:focus': {
                                                    outline: 'none'
                                                }
                                            },
                                            '& .MuiDataGrid-columnHeaders': {
                                                backgroundColor: 'rgba(0,0,0,0.02)',
                                                borderBottom: '1px solid rgba(0,0,0,0.1)',
                                                color: 'text.primary',
                                                '& .MuiDataGrid-columnHeader': {
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    padding: '12px 16px',
                                                    '&:focus': {
                                                        outline: 'none'
                                                    }
                                                }
                                            },
                                            '& .MuiDataGrid-row': {
                                                transition: 'background-color 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(25, 118, 210, 0.12)'
                                                    }
                                                }
                                            },
                                            '& .MuiDataGrid-footerContainer': {
                                                borderTop: '1px solid rgba(0,0,0,0.1)',
                                                backgroundColor: 'rgba(0,0,0,0.02)',
                                                '& .MuiTablePagination-root': {
                                                    fontSize: '0.75rem'
                                                }
                                            },
                                            '& .MuiDataGrid-cell:focus': {
                                                outline: 'none'
                                            },
                                            '& .MuiDataGrid-cell:focus-within': {
                                                outline: 'none'
                                            },
                                            '& .MuiIconButton-root': {
                                                transition: 'all 0.2s ease',
                                                padding: '4px',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                                }
                                            }
                                        }
                                    }}
                                />
                            ) : (
                                <mui.Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        padding: 3,
                                        textAlign: 'center'
                                    }}
                                >
                                    <mui.Typography
                                        variant="h6"
                                        sx={{ color: 'text.secondary', mb: 1 }}
                                    >
                                        No Workpapers Found
                                    </mui.Typography>
                                    <mui.Typography
                                        variant="body2"
                                        sx={{ color: 'text.secondary', maxWidth: '500px' }}
                                    >
                                        {selectedApp ? 
                                            `No workpapers available for ${selectedApp.APP_NAME}. Try selecting a different application or check that workpapers have been created.` :
                                            'No workpapers available. Try selecting a specific application or check that workpapers have been created.'}
                                    </mui.Typography>
                                </mui.Box>
                            )}
                        </mui.Box>
                    ) : (
                        <mui.Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 2,
                                width: '100%',
                                height: '100%',
                                justifyContent: 'center'
                            }}
                        >
                            <CircularProgress 
                                size={40} 
                                thickness={4}
                                sx={{
                                    color: 'primary.main',
                                    animation: 'pulse 1.5s ease-in-out infinite',
                                }}
                            />
                            <mui.Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    animation: 'fadeIn 0.3s ease-in-out',
                                }}
                            >
                                Loading {selectedApp?.APP_NAME || 'workpapers'}...
                            </mui.Typography>
                        </mui.Box>
                    )}
                </mui.Box>
            </Suspense>
        ) : tab.content
    }));

    const handleAppChange = async (event, newValue) => {
        setIsDataLoading(true);
        
        if (newValue) {
            // User selected a specific app
            setSelectedApp(newValue);
            setAppID(newValue.id);
            setWorkpapers([]); // Clear workpapers while loading
            setFilteredWorkpapers([]); // Also clear filtered workpapers
            await fetchWorkpapers(newValue.id, companyID);
        } else {
            // User selected to view all apps (null selection)
            setSelectedApp(null);
            setAppID(null);
            setWorkpapers([]); // Clear workpapers while loading
            setFilteredWorkpapers([]); // Also clear filtered workpapers
            await fetchAllWorkpapers(companyID);
        }
    };

    const handleFilterClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const handleFilterChange = (filterType, value) => {
        setSelectedFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
    };

    const getActiveFilterCount = () => {
        return Object.values(selectedFilters).filter(value => value !== 'All').length;
    };

    const handleClearFilters = () => {
        setSelectedFilters({
            type: 'All',
            status: 'All',
            assignee: 'All'
        });
        setAssigneeSearch('');
    };

    const renderFilterChip = () => {
        const activeCount = getActiveFilterCount();
        const isActive = activeCount > 0;
        
        return (
            <>
                <Chip 
                    icon={<FilterAltIcon />} 
                    label={isActive ? `Filter (${activeCount})` : 'Filter'}
                    variant={isActive ? "filled" : "outlined"}
                    onClick={handleFilterClick}
                    size="small"
                    sx={{ 
                        borderRadius: '6px',
                        borderColor: isActive ? 'primary.main' : 'rgba(0,0,0,0.1)',
                        color: isActive ? 'white' : 'text.primary',
                        backgroundColor: isActive ? 'primary.main' : 'transparent',
                        fontSize: '0.8125rem',
                        height: '28px',
                        transition: 'all 0.2s ease',
                        '& .MuiSvgIcon-root': {
                            fontSize: '1rem',
                            color: isActive ? 'white' : 'inherit'
                        },
                        '&:hover': { 
                            backgroundColor: isActive ? 'primary.dark' : 'rgba(25, 118, 210, 0.04)',
                            borderColor: 'primary.main'
                        }
                    }}
                />
                <mui.Menu
                                     anchorEl={anchorEl}
                                     open={Boolean(anchorEl)}
                                     onClose={handleFilterClose}
                                     PaperProps={{
                                         elevation: 3,
                                         sx: {
                                             minWidth: 280,
                                             maxHeight: '70vh',
                                             overflow: 'auto',
                                             mt: 1,
                                             '& .MuiMenuItem-root': {
                                                 py: 1
                                             }
                                         }
                                     }}
                                 >
                                     {getActiveFilterCount() > 0 && (
                                         <mui.Box sx={{ 
                                             px: 2, 
                                             py: 1, 
                                             display: 'flex', 
                                             justifyContent: 'flex-end',
                                             borderBottom: '1px solid rgba(0,0,0,0.08)'
                                         }}>
                                             <mui.Button
                                                 size="small"
                                                 onClick={handleClearFilters}
                                                 startIcon={<mui.Icon sx={{ fontSize: '1rem' }}>clear</mui.Icon>}
                                                 sx={{
                                                     minWidth: 'auto',
                                                     textTransform: 'none',
                                                     fontSize: '0.75rem',
                                                     fontWeight: 500,
                                                     color: 'text.secondary',
                                                     p: '4px 8px',
                                                     '&:hover': {
                                                         backgroundColor: 'rgba(0,0,0,0.04)',
                                                         color: 'text.primary'
                                                     }
                                                 }}
                                             >
                                                 Clear All
                                             </mui.Button>
                                         </mui.Box>
                                     )}
                                     {[
                                         {
                                             label: 'Type',
                                             options: filterOptions.types,
                                             value: selectedFilters.type,
                                             onChange: (value) => handleFilterChange('type', value)
                                         },
                                         {
                                             label: 'Status',
                                             options: filterOptions.statuses,
                                             value: selectedFilters.status,
                                             onChange: (value) => handleFilterChange('status', value)
                                         },
                                         {
                                             label: 'Assignee',
                                             options: filterOptions.assignees,
                                             value: selectedFilters.assignee,
                                             onChange: (value) => handleFilterChange('assignee', value),
                                             searchable: true
                                         }
                                     ].map((filter, index, array) => (
                                         <React.Fragment key={filter.label}>
                                             <mui.Typography
                                                 variant="subtitle2"
                                                 sx={{
                                                     px: 2,
                                                     py: 1,
                                                     color: 'text.secondary',
                                                     fontSize: '0.75rem',
                                                     fontWeight: 600,
                                                     bgcolor: 'rgba(0,0,0,0.02)'
                                                 }}
                                             >
                                                 {filter.label}
                                             </mui.Typography>
                                             {filter.searchable && (
                                                 <mui.Box sx={{ px: 2, py: 1 }}>
                                                     <TextField
                                                         size="small"
                                                         fullWidth
                                                         placeholder="Search assignee..."
                                                         value={assigneeSearch}
                                                         onChange={(e) => setAssigneeSearch(e.target.value)}
                                                         InputProps={{
                                                             startAdornment: (
                                                                 <mui.InputAdornment position="start">
                                                                     <mui.Icon sx={{ fontSize: '1.25rem', color: 'text.secondary' }}>
                                                                         search
                                                                     </mui.Icon>
                                                                 </mui.InputAdornment>
                                                             ),
                                                         }}
                                                         sx={{
                                                             '& .MuiOutlinedInput-root': {
                                                                 borderRadius: '6px',
                                                                 fontSize: '0.875rem',
                                                                 backgroundColor: 'rgba(0,0,0,0.02)',
                                                                 '&:hover': {
                                                                     backgroundColor: 'rgba(0,0,0,0.04)'
                                                                 }
                                                             }
                                                         }}
                                                     />
                                                 </mui.Box>
                                             )}
                                             {filter.options
                                                 .filter(option => 
                                                     !filter.searchable || 
                                                     assigneeSearch === '' || 
                                                     option.toLowerCase().includes(assigneeSearch.toLowerCase())
                                                 )
                                                 .map((option) => (
                                                     <mui.MenuItem
                                                         key={option}
                                                         onClick={() => {
                                                             filter.onChange(option);
                                                             if (option === 'All') {
                                                                 handleFilterClose();
                                                             }
                                                             if (filter.searchable) {
                                                                 setAssigneeSearch('');
                                                             }
                                                         }}
                                                         sx={{
                                                             fontSize: '0.875rem',
                                                             color: filter.value === option ? 'primary.main' : 'text.primary',
                                                             fontWeight: filter.value === option ? 600 : 400
                                                         }}
                                                     >
                                                         <mui.Box
                                                             component="span"
                                                             sx={{
                                                                 width: 20,
                                                                 height: 20,
                                                                 mr: 1,
                                                                 display: 'inline-flex',
                                                                 alignItems: 'center',
                                                                 justifyContent: 'center'
                                                             }}
                                                         >
                                                             {filter.value === option && (
                                                                 <mui.Icon sx={{ fontSize: '1.25rem', color: 'primary.main' }}>
                                                                     check
                                                                 </mui.Icon>
                                                             )}
                                                         </mui.Box>
                                                         {option}
                                                     </mui.MenuItem>
                                                 ))}
                                             {index < array.length - 1 && (
                                                 <mui.Divider sx={{ my: 1 }} />
                                             )}
                                         </React.Fragment>
                                     ))}
                                 </mui.Menu>
            </>
        );
    };

    const generateStatsData = () => {
        if (!Array.isArray(filteredWorkpapers)) return { statusData: [], typeData: [], assigneeData: [] };

        // Generate status statistics
        const statusCount = filteredWorkpapers.reduce((acc, wp) => {
            const status = wp.STATUS || 'Not Set';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        const statusData = Object.entries(statusCount).map(([name, value]) => ({
            name,
            value,
            color: name.toLowerCase() === 'complete' ? '#10b981' :
                   name.toLowerCase() === 'in progress' ? '#3b82f6' :
                   name.toLowerCase() === 'not started' ? '#f59e0b' :
                   name.toLowerCase() === 'in review' ? '#6366f1' :
                   name.toLowerCase() === 'rejected' ? '#ef4444' : '#94a3b8'
        }));

        // Generate type statistics
        const typeCount = filteredWorkpapers.reduce((acc, wp) => {
            const type = wp.TYPE || 'Not Set';
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        }, {});

        const typeData = Object.entries(typeCount).map(([name, value]) => ({
            name,
            value,
            color: '#' + Math.floor(Math.random()*16777215).toString(16)
        }));

        // Generate assignee statistics with status breakdown
        const assigneeData = filteredWorkpapers.reduce((acc, wp) => {
            const workpaperDetails = Array.isArray(wp.WORKPAPER_DETAILS) ? wp.WORKPAPER_DETAILS[0] : wp.WORKPAPER_DETAILS;
            const assignee = workpaperDetails?.ASSIGNED_TO || 'Unassigned';
            const status = wp.STATUS?.toLowerCase() || 'not set';

            if (!acc[assignee]) {
                acc[assignee] = {
                    name: userDetails[assignee] || assignee,
                    total: 0,
                    complete: 0,
                    'in progress': 0,
                    'not started': 0,
                    'in review': 0,
                    other: 0
                };
            }

            acc[assignee].total += 1;
            if (status === 'complete') acc[assignee].complete += 1;
            else if (status === 'in progress') acc[assignee]['in progress'] += 1;
            else if (status === 'not started') acc[assignee]['not started'] += 1;
            else if (status === 'in review') acc[assignee]['in review'] += 1;
            else acc[assignee].other += 1;

            return acc;
        }, {});

        return { 
            statusData, 
            typeData, 
            assigneeData: Object.values(assigneeData).sort((a, b) => b.total - a.total)
        };
    };

    const StatsDialog = ({ open, onClose }) => {
        const { statusData, typeData, assigneeData } = generateStatsData();
        const totalWorkpapers = filteredWorkpapers?.length || 0;

        return (
            <mui.Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxHeight: '90vh'
                    }
                }}
            >
                <mui.DialogTitle sx={{ 
                    pb: 2, 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimelineOutlinedIcon color="primary" />
                        <mui.Typography variant="h6">Workpaper Statistics</mui.Typography>
                    </mui.Box>
                    <mui.IconButton
                        edge="end"
                        onClick={onClose}
                        aria-label="close"
                        size="small"
                    >
                        <CloseIcon />
                    </mui.IconButton>
                </mui.DialogTitle>
                <mui.DialogContent sx={{ py: 3 }}>
                    <mui.Grid container spacing={3}>
                        <mui.Grid item xs={12}>
                            <mui.Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: '#f8fafc'
                                }}
                            >
                                <mui.Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                                    Overview
                                </mui.Typography>
                                <mui.Grid container spacing={2}>
                                    <mui.Grid item xs={12} sm={4}>
                                        <mui.Box sx={{ textAlign: 'center', p: 2 }}>
                                            <mui.Typography variant="h4" sx={{ mb: 1, color: 'primary.main' }}>
                                                {totalWorkpapers}
                                            </mui.Typography>
                                            <mui.Typography variant="body2" color="text.secondary">
                                                Total Workpapers
                                            </mui.Typography>
                                        </mui.Box>
                                    </mui.Grid>
                                    <mui.Grid item xs={12} sm={4}>
                                        <mui.Box sx={{ textAlign: 'center', p: 2 }}>
                                            <mui.Typography variant="h4" sx={{ mb: 1, color: '#10b981' }}>
                                                {statusData.find(s => s.name.toLowerCase() === 'complete')?.value || 0}
                                            </mui.Typography>
                                            <mui.Typography variant="body2" color="text.secondary">
                                                Completed
                                            </mui.Typography>
                                        </mui.Box>
                                    </mui.Grid>
                                    <mui.Grid item xs={12} sm={4}>
                                        <mui.Box sx={{ textAlign: 'center', p: 2 }}>
                                            <mui.Typography variant="h4" sx={{ mb: 1, color: '#f59e0b' }}>
                                                {statusData.find(s => s.name.toLowerCase() === 'in progress')?.value || 0}
                                            </mui.Typography>
                                            <mui.Typography variant="body2" color="text.secondary">
                                                In Progress
                                            </mui.Typography>
                                        </mui.Box>
                                    </mui.Grid>
                                </mui.Grid>
                            </mui.Paper>
                        </mui.Grid>
                        <mui.Grid item xs={12}>
                            <mui.Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2
                                }}
                            >
                                <mui.Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                                    Assignee Workload
                                </mui.Typography>
                                <mui.TableContainer>
                                    <mui.Table size="small">
                                        <mui.TableHead>
                                            <mui.TableRow>
                                                <mui.TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Assignee</mui.TableCell>
                                                <mui.TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Total</mui.TableCell>
                                                <mui.TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#10b981' }}>Complete</mui.TableCell>
                                                <mui.TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#3b82f6' }}>In Progress</mui.TableCell>
                                                <mui.TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#f59e0b' }}>Not Started</mui.TableCell>
                                                <mui.TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#6366f1' }}>In Review</mui.TableCell>
                                            </mui.TableRow>
                                        </mui.TableHead>
                                        <mui.TableBody>
                                            {assigneeData.map((assignee, index) => (
                                                <mui.TableRow key={index} sx={{
                                                    '&:nth-of-type(odd)': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                    }
                                                }}>
                                                    <mui.TableCell sx={{ fontSize: '0.75rem' }}>{assignee.name}</mui.TableCell>
                                                    <mui.TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{assignee.total}</mui.TableCell>
                                                    <mui.TableCell align="center" sx={{ fontSize: '0.75rem' }}>{assignee.complete}</mui.TableCell>
                                                    <mui.TableCell align="center" sx={{ fontSize: '0.75rem' }}>{assignee['in progress']}</mui.TableCell>
                                                    <mui.TableCell align="center" sx={{ fontSize: '0.75rem' }}>{assignee['not started']}</mui.TableCell>
                                                    <mui.TableCell align="center" sx={{ fontSize: '0.75rem' }}>{assignee['in review']}</mui.TableCell>
                                                </mui.TableRow>
                                            ))}
                                        </mui.TableBody>
                                    </mui.Table>
                                </mui.TableContainer>
                            </mui.Paper>
                        </mui.Grid>
                        <mui.Grid item xs={12} md={6}>
                            <mui.Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2
                                }}
                            >
                                <mui.Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                                    Status Distribution
                                </mui.Typography>
                                <RechartsResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={statusData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={80}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {statusData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip
                                            content={({ active, payload }) => {
                                                if (active && payload && payload.length) {
                                                    const data = payload[0].payload;
                                                    return (
                                                        <mui.Box
                                                            sx={{
                                                                bgcolor: 'background.paper',
                                                                p: 1.5,
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 1,
                                                                boxShadow: 1
                                                            }}
                                                        >
                                                            <mui.Typography variant="body2" sx={{ color: data.color }}>
                                                                {data.name}: {data.value}
                                                            </mui.Typography>
                                                        </mui.Box>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                    </PieChart>
                                </RechartsResponsiveContainer>
                                <mui.Box sx={{ mt: 2 }}>
                                    {statusData.map((entry) => (
                                        <mui.Box
                                            key={entry.name}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 1
                                            }}
                                        >
                                            <mui.Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: '50%',
                                                    bgcolor: entry.color
                                                }}
                                            />
                                            <mui.Typography variant="caption">
                                                {entry.name} ({entry.value})
                                            </mui.Typography>
                                        </mui.Box>
                                    ))}
                                </mui.Box>
                            </mui.Paper>
                        </mui.Grid>
                        <mui.Grid item xs={12} md={6}>
                            <mui.Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2
                                }}
                            >
                                <mui.Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                                    Type Distribution
                                </mui.Typography>
                                <RechartsResponsiveContainer width="100%" height={300}>
                                    <BarChart
                                        data={typeData}
                                        margin={{
                                            top: 5,
                                            right: 30,
                                            left: 20,
                                            bottom: 5,
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" />
                                        <YAxis />
                                        <RechartsTooltip
                                            content={({ active, payload, label }) => {
                                                if (active && payload && payload.length) {
                                                    return (
                                                        <mui.Box
                                                            sx={{
                                                                bgcolor: 'background.paper',
                                                                p: 1.5,
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 1,
                                                                boxShadow: 1
                                                            }}
                                                        >
                                                            <mui.Typography variant="body2">
                                                                {label}: {payload[0].value}
                                                            </mui.Typography>
                                                        </mui.Box>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Bar dataKey="value">
                                            {typeData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </RechartsResponsiveContainer>
                            </mui.Paper>
                        </mui.Grid>
                    </mui.Grid>
                </mui.DialogContent>
            </mui.Dialog>
        );
    };

    const fetchAuditUsers = async () => {
        try {
            const users = await roleService.fetchUsersByAudit(id);
            if (users) {
                const formattedUsers = users.map(user => ({
                    id: user.id,
                    email: user?.user?.email || '',
                    fullName: user?.user ? 
                        `${user.user.first_name || ''} ${user.user.last_name || ''}`.trim() : 
                        'Unknown User',
                    displayName: user?.user ? 
                        `${user.user.first_name || ''} ${user.user.last_name || ''} (${user?.user?.email || ''})`.trim() : 
                        'Unknown User'
                })).filter(user => user.fullName !== 'Unknown User');
                setAuditUsers(formattedUsers);
            }
        } catch (error) {
            console.error('Error fetching audit users:', error);
        }
    };

    useEffect(() => {
        if (id) {
            fetchAuditUsers();
        }
    }, [id]);

    const handleBulkAssign = async () => {
        if (!selectedAssignee || selectedRows.length === 0) return;

        setAssignmentLoading(true);
        try {
            // Get the workpapers that were selected
            const selectedWorkpapers = rows.filter(row => selectedRows.includes(row.id));
            
            // Update each selected workpaper
            await Promise.all(selectedWorkpapers.map(async (workpaper) => {
                try {
                    // First try to fetch existing workpaper details using the workpaper ID
                    const existingDetails = await auditService.fetchWorkpaperDetailsById(workpaper.ctrlID);
                    
                    const workpaperDetails = {
                        AUDIT_FILE: workpaper.ctrlID,
                        ASSIGNED_TO: selectedAssignee
                    };

                    if (existingDetails && existingDetails.length > 0) {
                        // If details exist, update them
                        await auditService.updateWorkpaperDetails(existingDetails[0].id, workpaperDetails);
                    } else {
                        // If no details exist, create new ones
                        await auditService.createWorkpaperDetails({
                            ...workpaperDetails,
                            workpaper: workpaper.ctrlID
                        });
                    }
                } catch (error) {
                    console.error(`Error processing workpaper ${workpaper.ctrlID}:`, error);
                    throw error;
                }
            }));

            // Refresh workpapers
            await fetchWorkpapers(appID, companyID);
            
            setSnackbar({
                open: true,
                message: 'Workpapers assigned successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error assigning workpapers:', error);
            setSnackbar({
                open: true,
                message: 'Error assigning workpapers',
                severity: 'error'
            });
        } finally {
            setAssignmentLoading(false);
            setAssignmentDialogOpen(false);
            setSelectedAssignee('');
            setSelectedRows([]);
        }
    };

    const AssignmentDialog = () => {
        const [searchQuery, setSearchQuery] = useState('');
        
        const filteredUsers = auditUsers.filter(user => 
            user.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email.toLowerCase().includes(searchQuery.toLowerCase())
        );

        return (
            <mui.Dialog 
                open={assignmentDialogOpen} 
                onClose={() => setAssignmentDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <mui.DialogTitle sx={{ pb: 1 }}>
                    Assign Workpapers
                </mui.DialogTitle>
                <mui.DialogContent>
                    <mui.Typography variant="body2" sx={{ mb: 2 }}>
                        Selected workpapers: {selectedRows.length}
                    </mui.Typography>
                    <mui.TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                '& fieldset': {
                                    borderColor: 'rgba(0, 0, 0, 0.1)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(0, 0, 0, 0.2)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'primary.main',
                                },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <mui.InputAdornment position="start">
                                    <mui.SvgIcon
                                        fontSize="small"
                                        color="action"
                                    >
                                        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                                    </mui.SvgIcon>
                                </mui.InputAdornment>
                            ),
                        }}
                    />
                    <mui.List sx={{ 
                        width: '100%',
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        maxHeight: '400px',
                        overflow: 'auto'
                    }}>
                        {filteredUsers.map((user) => (
                            <mui.ListItem 
                                key={user.email}
                                disablePadding
                                sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    '&:last-child': {
                                        borderBottom: 'none'
                                    }
                                }}
                            >
                                <mui.ListItemButton
                                    onClick={() => setSelectedAssignee(user.email)}
                                    selected={selectedAssignee === user.email}
                                    sx={{
                                        '&.Mui-selected': {
                                            backgroundColor: 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                            }
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                        }
                                    }}
                                >
                                    <mui.ListItemAvatar>
                                        <mui.Avatar sx={{ bgcolor: selectedAssignee === user.email ? 'primary.main' : 'grey.400' }}>
                                            {`${user.fullName.split(' ')[0][0]}${user.fullName.split(' ').pop()[0]}`}
                                        </mui.Avatar>
                                    </mui.ListItemAvatar>
                                    <mui.ListItemText 
                                        primary={user.fullName}
                                        secondary={user.email}
                                        primaryTypographyProps={{
                                            fontWeight: selectedAssignee === user.email ? 600 : 400,
                                            color: selectedAssignee === user.email ? 'primary.main' : 'text.primary'
                                        }}
                                    />
                                    {selectedAssignee === user.email && (
                                        <CheckCircleIcon 
                                            sx={{ 
                                                color: 'primary.main'
                                            }}
                                        />
                                    )}
                                </mui.ListItemButton>
                            </mui.ListItem>
                        ))}
                    </mui.List>
                </mui.DialogContent>
                <mui.DialogActions>
                    <mui.Button 
                        onClick={() => {
                            setAssignmentDialogOpen(false);
                            setSearchQuery('');
                        }}
                        disabled={assignmentLoading}
                    >
                        Cancel
                    </mui.Button>
                    <mui.Button 
                        onClick={handleBulkAssign}
                        variant="contained"
                        disabled={!selectedAssignee || assignmentLoading}
                        startIcon={assignmentLoading ? <CircularProgress size={20} /> : null}
                    >
                        {assignmentLoading ? 'Assigning...' : 'Assign'}
                    </mui.Button>
                </mui.DialogActions>
            </mui.Dialog>
        );
    };

    const Snackbar = () => (
        <mui.Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{
                top: '24px'
            }}
        >
            <mui.Alert 
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
                severity={snackbar.severity}
                sx={{ 
                    width: '100%',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                }}
            >
                {snackbar.message}
            </mui.Alert>
        </mui.Snackbar>
    );

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Box sx={{ 
                    padding: { xs: '16px', sm: '24px' },
                    maxWidth: '1600px',
                    margin: '0 auto'
                }}>
                    <mui.Breadcrumbs 
                        aria-label="breadcrumb"
                        sx={{
                            '& .MuiLink-root': {
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.secondary',
                                fontSize: '0.875rem',
                                transition: 'color 0.2s ease',
                                '&:hover': {
                                    color: 'primary.main'
                                }
                            }
                        }}
                    >
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href={`/Audit/Projects/${selectedProject?.id}`}>
                            {selectedProject?.AUDIT_NAME}
                        </mui.Link>
                        <mui.Typography color="text.primary">Workpapers</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        mb: 4, 
                        mt: 3,
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 2, sm: 0 }
                    }}>
                        <FolderSpecialRoundedIcon sx={{ 
                            fontSize: 28, 
                            color: 'primary.main', 
                            mr: { xs: 0, sm: 2.5 },
                            opacity: 0.9
                        }} />
                        <mui.Box sx={{ flex: 1 }}>
                            <mui.Typography 
                                variant="h4" 
                                component="h1" 
                                sx={{ 
                                    fontWeight: 600, 
                                    color: 'text.primary', 
                                    mb: 1,
                                    fontSize: { xs: '1.5rem', sm: '2rem' }
                                }}
                            >
                                Workpapers
                            </mui.Typography>
                            <mui.Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2,
                                flexWrap: 'wrap'
                            }}>
                                <mui.Chip
                                    size="small"
                                    label={selectedProject?.AUDIT_TYPE || 'N/A'}
                                    sx={{ 
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                        color: 'primary.main',
                                        fontWeight: 500,
                                        borderRadius: '6px',
                                        height: '24px'
                                    }}
                                />
                                <mui.Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1 
                                }}>
                                    <span style={{ 
                                        width: 6, 
                                        height: 6, 
                                        borderRadius: '50%',
                                        backgroundColor: selectedProject?.STATUS === 'Active' ? '#10b981' : 
                                                      selectedProject?.STATUS === 'Archived' ? '#6b7280' :
                                                      selectedProject?.STATUS === 'On-Hold' ? '#ef4444' : '#f59e0b',
                                        display: 'inline-block'
                                    }} />
                                    <mui.Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: 'text.secondary',
                                            fontSize: '0.8125rem'
                                        }}
                                    >
                                        {selectedProject?.STATUS || 'N/A'}
                                    </mui.Typography>
                                </mui.Box>
                            </mui.Box>
                        </mui.Box>
                    </mui.Box>

                    {isLoading ? (
                        <mui.Box 
                            sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                height: '60vh',
                                backgroundColor: '#ffffff',
                                borderRadius: 2,
                                boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
                                border: '1px solid',
                                borderColor: 'rgba(0,0,0,0.06)'
                            }}
                        >
                            <mui.Box sx={{ textAlign: 'center' }}>
                                <CircularProgress 
                                    size={40} 
                                    thickness={4}
                                    sx={{
                                        color: 'primary.main',
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                    }}
                                />
                                <mui.Typography 
                                    variant="body1" 
                                    sx={{ 
                                        mt: 2, 
                                        color: 'text.secondary',
                                        fontWeight: 500
                                    }}
                                >
                                    Loading Workpapers...
                                </mui.Typography>
                            </mui.Box>
                        </mui.Box>
                    ) : (
                        <mui.Box sx={{ 
                            backgroundColor: '#ffffff', 
                            borderRadius: 2,
                            minHeight: '80vh',
                            border: '1px solid',
                            borderColor: 'rgba(0,0,0,0.06)',
                            boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)'
                        }}>
                            <mui.Paper 
                                elevation={0}
                                sx={{ 
                                    p: { xs: 2, sm: 3 },
                                    height: '100%',
                                    backgroundColor: 'transparent'
                                }}
                            >
                                <mui.Box sx={{ mb: 3 }}>
                                    <mui.Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <mui.Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                            Application Filter
                                        </mui.Typography>
                                        {selectedApp ? (
                                            <mui.Chip
                                                label="Filtered View"
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                onDelete={() => handleAppChange(null, null)}
                                                sx={{ height: 24, fontSize: '0.75rem' }}
                                            />
                                        ) : (
                                            <mui.Chip
                                                label="Showing All Applications"
                                                size="small"
                                                color="default"
                                                variant="outlined"
                                                sx={{ height: 24, fontSize: '0.75rem', bgcolor: 'rgba(0,0,0,0.03)' }}
                                            />
                                        )}
                                    </mui.Box>
                                    <Autocomplete
                                        fullWidth
                                        options={apps}
                                        getOptionLabel={(option) => option.APP_NAME || ''}
                                        value={selectedApp}
                                        onChange={handleAppChange}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Filter by Application"
                                                placeholder="Select an application to filter or leave empty to view all"
                                                size="medium"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        backgroundColor: '#ffffff',
                                                        borderRadius: 1.5,
                                                        transition: 'all 0.2s ease',
                                                        border: '1px solid',
                                                        borderColor: 'rgba(0,0,0,0.1)',
                                                        '&:hover': {
                                                            borderColor: 'rgba(25, 118, 210, 0.5)',
                                                            backgroundColor: '#ffffff'
                                                        },
                                                        '&.Mui-focused': {
                                                            borderColor: 'primary.main',
                                                            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)'
                                                        }
                                                    },
                                                    '& .MuiInputLabel-root': {
                                                        color: 'text.secondary',
                                                        fontSize: '0.875rem'
                                                    }
                                                }}
                                            />
                                        )}
                                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                        clearOnEscape
                                        clearText="Show all applications"
                                        renderOption={(props, option) => (
                                            <li {...props}>
                                                {option.APP_NAME}
                                            </li>
                                        )}
                                    />
                                </mui.Box>

                                <mui.Box sx={{ mb: 3 }}>
                                    <mui.Stack 
                                        direction={{ xs: 'column', sm: 'row' }} 
                                        spacing={2}
                                        alignItems={{ xs: 'stretch', sm: 'center' }}
                                        justifyContent="space-between"
                                    >
                                        <mui.Stack 
                                            direction="row" 
                                            spacing={1} 
                                            sx={{ 
                                                flexWrap: 'wrap',
                                                gap: 1
                                            }}
                                        >
                                            <Chip 
                                                label="My Queue"
                                                variant={activeFilter === 'myQueue' ? 'filled' : 'outlined'}
                                                onClick={() => setActiveFilter('myQueue')}
                                                size="small"
                                                sx={{ 
                                                    borderRadius: '6px',
                                                    borderColor: activeFilter === 'myQueue' ? 'primary.main' : 'rgba(0,0,0,0.1)',
                                                    color: activeFilter === 'myQueue' ? 'white' : 'text.primary',
                                                    backgroundColor: activeFilter === 'myQueue' ? 'primary.main' : 'transparent',
                                                    fontSize: '0.8125rem',
                                                    height: '28px',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': { 
                                                        backgroundColor: activeFilter === 'myQueue' ? 'primary.dark' : 'rgba(25, 118, 210, 0.04)',
                                                        borderColor: 'primary.main'
                                                    }
                                                }}
                                            />
                                            <Chip 
                                                label="Show All"
                                                variant={activeFilter === 'all' ? 'filled' : 'outlined'}
                                                onClick={() => setActiveFilter('all')}
                                                size="small"
                                                sx={{ 
                                                    borderRadius: '6px',
                                                    borderColor: activeFilter === 'all' ? 'primary.main' : 'rgba(0,0,0,0.1)',
                                                    color: activeFilter === 'all' ? 'white' : 'text.primary',
                                                    backgroundColor: activeFilter === 'all' ? 'primary.main' : 'transparent',
                                                    fontSize: '0.8125rem',
                                                    height: '28px',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': { 
                                                        backgroundColor: activeFilter === 'all' ? 'primary.dark' : 'rgba(25, 118, 210, 0.04)',
                                                        borderColor: 'primary.main'
                                                    }
                                                }}
                                            />
                                        </mui.Stack>
                                        <mui.Stack direction="row" spacing={1}>
                                            {[
                                                { icon: <TimelineOutlinedIcon />, label: 'Stats', onClick: () => setStatsDialogOpen(true) }
                                            ].map(({ icon, label, onClick }) => (
                                                <Chip 
                                                    key={label}
                                                    icon={icon} 
                                                    label={label} 
                                                    variant="outlined" 
                                                    onClick={onClick} 
                                                    size="small"
                                                    sx={{ 
                                                        borderRadius: '6px',
                                                        borderColor: 'rgba(0,0,0,0.1)',
                                                        color: 'text.primary',
                                                        backgroundColor: 'transparent',
                                                        fontSize: '0.8125rem',
                                                        height: '28px',
                                                        transition: 'all 0.2s ease',
                                                        '& .MuiSvgIcon-root': {
                                                            fontSize: '1rem'
                                                        },
                                                        '&:hover': { 
                                                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                            borderColor: 'primary.main'
                                                        }
                                                    }}
                                                />
                                            ))}
                                            <Chip 
                                                icon={<AssignmentTurnedInIcon />}
                                                label={selectedRows.length > 0 ? `Assign (${selectedRows.length})` : 'Assign'}
                                                onClick={() => {
                                                    if (selectedRows.length === 0) {
                                                        setSnackbar({
                                                            open: true,
                                                            message: 'Please select workpapers to assign',
                                                            severity: 'warning'
                                                        });
                                                        return;
                                                    }
                                                    setAssignmentDialogOpen(true);
                                                }}
                                                size="small"
                                                sx={{ 
                                                    borderRadius: '6px',
                                                    backgroundColor: selectedRows.length > 0 ? 'primary.main' : 'transparent',
                                                    color: selectedRows.length > 0 ? 'white' : 'text.secondary',
                                                    fontSize: '0.8125rem',
                                                    height: '28px',
                                                    transition: 'all 0.2s ease',
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: '1rem',
                                                        color: selectedRows.length > 0 ? 'white' : 'text.secondary'
                                                    },
                                                    '&:hover': { 
                                                        backgroundColor: selectedRows.length > 0 ? 'primary.dark' : 'rgba(0,0,0,0.04)',
                                                        cursor: selectedRows.length > 0 ? 'pointer' : 'not-allowed'
                                                    }
                                                }}
                                            />
                                            {renderFilterChip()}
                                        </mui.Stack>
                                    </mui.Stack>
                                </mui.Box>

                                <mui.Paper 
                                    elevation={0} 
                                    sx={{ 
                                        backgroundColor: '#ffffff',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        border: '1px solid',
                                        borderColor: 'rgba(0,0,0,0.1)',
                                        transition: 'border-color 0.2s ease',
                                        '&:hover': {
                                            borderColor: 'rgba(25, 118, 210, 0.2)'
                                        }
                                    }}
                                >
                                    <DynamicTabs 
                                        tabs={tabs}
                                        sx={{
                                            '& .MuiTabs-root': {
                                                borderBottom: '1px solid',
                                                borderColor: 'rgba(0,0,0,0.1)',
                                                backgroundColor: 'rgba(0,0,0,0.02)',
                                                minHeight: '48px'
                                            },
                                            '& .MuiTab-root': {
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: '0.875rem',
                                                minHeight: '48px',
                                                color: 'text.secondary',
                                                transition: 'all 0.2s ease',
                                                '&.Mui-selected': {
                                                    color: 'primary.main',
                                                    fontWeight: 600
                                                },
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                                    color: 'primary.main'
                                                }
                                            },
                                            '& .MuiTabs-indicator': {
                                                backgroundColor: 'primary.main',
                                                height: '2px'
                                            }
                                        }}
                                    />
                                </mui.Paper>
                            </mui.Paper>
                        </mui.Box>
                    )}
                </mui.Box>
            </ResponsiveContainer>
            <StatsDialog open={statsDialogOpen} onClose={() => setStatsDialogOpen(false)} />
            <AssignmentDialog />
            <Snackbar />
        </div>
    );

    // Function to purge all workpapers cache data
    const purgeWorkpapersCache = () => {
        try {
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('workpapers_')) {
                    localStorage.removeItem(key);
                }
            });
            return true;
        } catch (error) {
            console.error('Failed to purge workpapers cache:', error);
            return false;
        }
    };

    // Function to refresh data from server (purge cache and fetch)
    // This is an internal function that can be called programmatically
    // but is not exposed in the UI as per user preference
    const refreshWorkpapers = async () => {
        setIsDataLoading(true);
        purgeWorkpapersCache();
        setUsingCache(false);
        
        try {
            if (selectedApp) {
                await fetchWorkpapers(selectedApp.id, companyID);
            } else {
                await fetchAllWorkpapers(companyID);
            }
            
            setSnackbar({
                open: true,
                message: 'Workpapers refreshed successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error refreshing workpapers:', error);
            setSnackbar({
                open: true,
                message: 'Failed to refresh workpapers',
                severity: 'error'
            });
        }
    };

    return (
        <Suspense fallback={
            <mui.Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <CircularProgress />
                <mui.Typography 
                    variant="body1" 
                    sx={{ 
                        mt: 2, 
                        color: 'text.secondary',
                        fontWeight: 500
                    }}
                >
                    Loading Workpapers...
                </mui.Typography>
            </mui.Box>
        }>
            <div>
                <IASideBar mainContent={customMainContent} />
            </div>
        </Suspense>
    );
};

export default Workpapers;